import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import { supabase } from '../utils/supabaseClient';
import './Register.css';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.password) newErrors.password = 'Please enter your password';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      // 1. Авторизация через Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        throw authError;
      }

      // 2. Получаем данные пользователя из teachers или users
      const userId = authData.user?.id;
      if (!userId) {
        throw new Error('Failed to get user ID from authentication');
      }

      // Пробуем teachers (для преподавателей)
      const { data: teacherData, error: teacherError } = await supabase
        .from('teachers')
        .select('*')
        .eq('auth_user_id', userId)
        .single();

      let userData;
      
      if (!teacherError && teacherData) {
        // Это преподаватель
        userData = {
          id: teacherData.auth_user_id,
          email: teacherData.email,
          name: teacherData.name,
          role: 'teacher'
        };
      } else {
        // Пробуем users (для студентов)
        const { data: studentUserData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (!userError && studentUserData) {
          userData = {
            id: studentUserData.id,
            email: studentUserData.email,
            name: studentUserData.name,
            role: studentUserData.role || 'student'
          };
        } else {
          // Self-heal. Supabase's signup confirmation flow doesn't put
          // an access_token in the URL fragment for us, so
          // EmailConfirmation never finds a session and never inserts
          // teachers/users rows. By the time the user reaches /login
          // they've already confirmed their email, signInWithPassword
          // succeeds, but the row that should mirror auth.users is
          // missing. Recover by reading role/name from user_metadata
          // (set in Register.jsx options.data) and creating the row now
          // that auth.uid() matches the new user.
          const authEmail = authData.user?.email || formData.email;
          const meta = authData.user?.user_metadata || {};
          const role = meta.role || 'student';
          const name = meta.name || '';

          // Legacy `users` / `teachers` tables predate the Supabase Auth
          // migration and still require a non-null password_hash. We just
          // got formData.password from the sign-in form, so reuse it.
          const passwordHash = await bcrypt.hash(formData.password, 10);
          if (role === 'teacher') {
            const { error: insErr } = await supabase
              .from('teachers')
              .insert({ auth_user_id: userId, email: authEmail, name, phone: null, password_hash: passwordHash });
            if (insErr && insErr.code !== '23505') {
              console.warn('Self-heal teacher insert error:', insErr.message);
              throw new Error('User account not found. Please contact support.');
            }
            userData = { id: userId, email: authEmail, name, role: 'teacher' };
          } else {
            const { error: insErr } = await supabase
              .from('users')
              .insert({ id: userId, email: authEmail, name, role: 'student', password_hash: passwordHash });
            if (insErr && insErr.code !== '23505') {
              console.warn('Self-heal student users insert error:', insErr.message);
              throw new Error('User account not found. Please contact support.');
            }
            userData = { id: userId, email: authEmail, name, role: 'student' };
          }
          console.log('Self-heal: created missing', role, 'row for auth user', userId);
        }
      }

      console.log('User authenticated:', userData);
      
      // 3. Сохраняем в localStorage для совместимости с существующим кодом
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      
      // 4. Redirect based on user role
      if (userData.role === 'student') {
        console.log('Student login detected, user ID:', userData.id);
        console.log('Looking for student in database...');
        
        // Ищем студента в Supabase по user_account_id
        const { getStudentByUserId } = await import('../utils/studentsService');
        let student = await getStudentByUserId(userData.id);

        console.log('Student data from database:', student);

        // ── Safety-net linking ────────────────────────────────────────────
        // EmailConfirmation is supposed to set user_account_id via the
        // update_student_on_registration RPC. If it didn't run (wrong
        // redirect URL, network blip, etc.), the student row for this
        // email still exists but is unlinked. Try to link it now that we
        // have a valid session and matching email.
        if (!student && userData.email) {
          try {
            const { data: matchByEmail } = await supabase
              .from('students')
              .select('id, invite_slug, name, email')
              .eq('email', userData.email)
              .is('user_account_id', null)
              .maybeSingle();
            if (matchByEmail?.id) {
              console.log('Found unlinked student by email — linking now:', matchByEmail.id);
              const { error: linkErr } = await supabase.rpc('update_student_on_registration', {
                p_student_id: matchByEmail.id,
                p_user_account_id: userData.id,
                p_email: userData.email,
                p_name: userData.name || matchByEmail.name
              });
              if (linkErr) console.warn('Safety-net link RPC failed:', linkErr.message);
              student = await getStudentByUserId(userData.id);
            }
          } catch (linkExc) {
            console.warn('Safety-net link exception:', linkExc?.message);
          }
        }

        if (student && student.invite_slug) {
          console.log('Student dashboard found, redirecting to:', `/student-dashboard/${student.invite_slug}`);
          // Обновляем имя из таблицы students (это имя как на платформе)
          if (student.name) {
            localStorage.setItem('currentUser', JSON.stringify({ ...userData, name: student.name }));
          }
          // Используем window.location.href для полного редиректа
          window.location.href = `/student-dashboard/${student.invite_slug}`;
          return; // Не продолжаем выполнение
        } else {
          console.error('Student dashboard not found for user:', userData.id);
          console.error('This might mean:');
          console.error('1. Student was not properly registered (user_account_id not set)');
          console.error('2. Student record does not exist in students table');
          console.error('3. user_account_id in students table does not match user ID');
          
          // Показываем более информативную ошибку
          setErrors({ 
            general: 'Student profile not found. Please make sure you registered through the invitation link from your teacher.' 
          });
          setIsLoading(false);
          return;
        }
      } else {
        console.log('Teacher/other role login, redirecting to home');
        // Для преподавателей используем обычную навигацию
        navigate('/');
        window.location.reload();
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.message || '';
      // Supabase returns "Email not confirmed" when "Confirm email" is on
      // and the user has not clicked the link yet.
      if (/email.*not.*confirmed|not.*confirmed/i.test(msg)) {
        setNeedsConfirmation(true);
        setErrors({});
      } else {
        setErrors({ email: msg || 'Invalid email or password' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!formData.email) {
      setErrors({ email: 'Please enter your email first.' });
      return;
    }
    setResendStatus('sending');
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` }
      });
      if (error) throw error;
      setResendStatus('sent');
      setTimeout(() => setResendStatus(null), 4000);
    } catch (e) {
      console.warn('Resend confirmation failed:', e?.message);
      setResendStatus('error');
      setTimeout(() => setResendStatus(null), 4000);
    }
  };

  return (
    <div className="register-split">
      <div className="register-video">
        <video
          src={process.env.PUBLIC_URL + '/Registration.mp4'}
          autoPlay
          loop
          muted
          playsInline
          className="register-bg-video"
        />
      </div>
      <div className="register-side">
        <div className="register-container">
          <div className="register-card">
            <div className="register-tabs">
              <Link to="/register" className="tab">Sign Up</Link>
              <button className="tab active">Sign In</button>
            </div>
            {needsConfirmation && (
              <div style={{
                margin: '0 0 16px',
                padding: '12px 14px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                border: '1px solid #fde68a',
                color: '#78350f',
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                <strong>Email not confirmed yet.</strong>
                <p style={{ margin: '4px 0 10px' }}>
                  Check your inbox for the confirmation link we sent to <b>{formData.email}</b>, or resend it:
                </p>
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendStatus === 'sending'}
                  style={{
                    padding: '7px 14px',
                    borderRadius: 8,
                    border: '1px solid #d97706',
                    background: '#fff',
                    color: '#92400e',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: resendStatus === 'sending' ? 'wait' : 'pointer',
                  }}
                >
                  {resendStatus === 'sending' ? 'Sending…'
                    : resendStatus === 'sent' ? '✓ Sent — check inbox'
                    : resendStatus === 'error' ? 'Failed — try again'
                    : 'Resend confirmation email'}
                </button>
              </div>
            )}
            <form className="register-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  autoComplete="email"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className={errors.password ? 'error' : ''}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              <button
                type="submit"
                className={`register-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="login-loading-container">
                    <div className="login-spinner"></div>
                    <span className="login-loading-text">Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 